import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, concatMap, forkJoin, from, Observable, of, shareReplay } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { arrayDiff, arrayDiffIdCompare, isDefined, isUndefined, tapSubscribed } from '../utils';
import {
  FileCacheModel,
  IMenu,
  IMenuWithIcon,
  IMenuWithPages,
  IMenuWithPagesAndBanner,
  IMenuWithPagesAndIcon,
} from '../models';
import { AuthService } from './auth.service';
import { FileCacheService } from './file-cache.service';
import { WatchdogService } from './watchdog.service';
import { Intercom } from './intercom.service';
import { RestaurantTableService } from './restaurant-table.service';
import { WebsocketService } from './websocket.service';
import { MenuRepository } from '../repositories/menu.repository';
import { FileCacheRepository } from '../repositories/file-cache.repository';
import { hasMany, hasOne } from '../database/repository.operators';
import { ClusterService } from './cluster.service';

interface ToUpdate<T> {
  origin: T;
  value: T;
}

@Injectable()
export class MenusService {

  private readonly destroyRef = inject(DestroyRef);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly auth = inject(AuthService);
  private readonly watchdog = inject(WatchdogService);
  private readonly cluster = inject(ClusterService);
  private readonly filesCache = inject(FileCacheService);
  private readonly menuRepository = inject(MenuRepository);
  private readonly filesCacheRepository = inject(FileCacheRepository);
  private readonly intercom = inject(Intercom);
  private readonly restaurantTable = inject(RestaurantTableService);
  private readonly webSocket = inject(WebsocketService);

  private readonly logger = this.watchdog.tag('Menus', 'green');

  public readonly loading$ = new BehaviorSubject<boolean>(true);
  public readonly sync$ = new BehaviorSubject<boolean>(false);

  public readonly menus$ = this.menuRepository.all$().pipe(
    tap(() => {
      this.loading$.next(true);
    }),
    shareReplay(1),
  );

  public readonly menusWithIcon$ = this.menus$.pipe(
    tapSubscribed(() => {
      this.loading$.next(true);
    }),
    hasOne(
      this.filesCacheRepository,
      'url',
      'icon',
      'iconLocal',
    ),
    map((menus): IMenuWithIcon[] | undefined => {
      if (menus === undefined) {
        return undefined;
      }

      return (Array.isArray(menus) ? menus : [menus]).map(menu => {
        const iconFile = menu.iconLocal
          ? new FileCacheModel(menu.iconLocal)
          : undefined;

        const iconLocal = iconFile
          ? this.domSanitizer.bypassSecurityTrustUrl(iconFile.objectUrl)
          : undefined;

        return {
          ...menu,
          iconLocal
        };
      });
    }),
    tap(() => {
      this.loading$.next(false);
    }),
    shareReplay(1)
  );

  public readonly menusWithPages$ = this.menus$.pipe(
    tapSubscribed(() => {
      this.loading$.next(true);
    }),
    hasMany(
      this.filesCacheRepository,
      'url',
      'pages',
      'pagesLocal',
    ),
    map((menus): IMenuWithPages[] | undefined => {
      if (menus === undefined) {
        return undefined;
      }

      return (Array.isArray(menus) ? menus : [menus]).map(menu => {
        const pagesLocal = menu.pagesLocal
          .sort((a, b) => menu.pages.indexOf(a.url) - menu.pages.indexOf(b.url))
          .map((page) => {
          const pageFile = page ? new FileCacheModel(page) : undefined;
          return pageFile
            ? this.domSanitizer.bypassSecurityTrustUrl(pageFile.objectUrl)
            : undefined;
        });

        return {
          ...menu,
          pagesLocal: pagesLocal.filter(isDefined),
        };
      });
    }),
    tap(() => {
      this.loading$.next(false);
    }),
    shareReplay(1)
  );

  public readonly menusWithPagesAndIcons$ = this.menus$.pipe(
    tapSubscribed(() => {
      this.loading$.next(true);
    }),
    hasMany(
      this.filesCacheRepository,
      'url',
      'pages',
      'pagesLocal',
    ),
    map((menus): IMenuWithPages[] | undefined => {
      if (menus === undefined) {
        return undefined;
      }

      return (Array.isArray(menus) ? menus : [menus]).map(menu => {
        const pagesLocal = menu.pagesLocal
          .sort((a, b) => menu.pages.indexOf(a.url) - menu.pages.indexOf(b.url))
          .map((page) => {
          const pageFile = page ? new FileCacheModel(page) : undefined;
          return pageFile
            ? this.domSanitizer.bypassSecurityTrustUrl(pageFile.objectUrl)
            : undefined;
        });

        return {
          ...menu,
          pagesLocal: pagesLocal.filter(isDefined),
        };
      });
    }),
    hasOne(
      this.filesCacheRepository,
      'url',
      'icon',
      'iconLocal',
    ),
    map((menus): IMenuWithPagesAndIcon[] | undefined => {
      if (menus === undefined) {
        return undefined;
      }

      return (Array.isArray(menus) ? menus : [menus]).map(menu => {
        const iconFile = menu.iconLocal
          ? new FileCacheModel(menu.iconLocal)
          : undefined;

        const iconLocal = iconFile
          ? this.domSanitizer.bypassSecurityTrustUrl(iconFile.objectUrl)
          : undefined;

        return {
          ...menu,
          iconLocal
        };
      });
    }),
    tap(() => this.loading$.next(false)),
    shareReplay(1)
  );

  public menusWithPagesAndBanner$ = this.menusWithPages$.pipe(
    tapSubscribed(() => this.loading$.next(true)),
    hasOne(
      this.filesCacheRepository,
      'url',
      'banner',
      'bannerLocal',
    ),
    map((menus): IMenuWithPagesAndBanner[] | undefined => {
      if (menus === undefined) {
        return undefined;
      }

      return (Array.isArray(menus) ? menus : [menus]).map(menu => {
        const bannerFile = menu.bannerLocal
          ? new FileCacheModel(menu.bannerLocal)
          : undefined;

        const bannerLocal = bannerFile
          ? this.domSanitizer.bypassSecurityTrustUrl(bannerFile.objectUrl)
          : undefined;

        return {
          ...menu,
          bannerLocal
        };
      });
    }),
    map(menus => {
      if (!menus) return undefined;

      return menus;
    }),
    tap(() => this.loading$.next(false)),
    shareReplay(1),
  );

  public initialize(): void {
    this.logger.info('Initialize');

    // Clear menus on logout
    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.auth.logouted$),
      switchMap(() => this.clear()),
      tap(() => this.logger.info('Cleared menus on logout')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    // Listen websocket messages
    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.webSocket.messages$),
      filter((response) => response.type === 'tableInfo'),
      switchMap((response) => {
        if (response.data.menus) {
          return this.sync(response.data.menus);
        } else {
          return this.clear();
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  public getMenuWithPages(id: number): Observable<IMenuWithPages | undefined> {
    return this.menuRepository.one$(id).pipe(
      tapSubscribed(() => {
        this.loading$.next(true);
      }),
      hasMany(
        this.filesCacheRepository,
        'url',
        'pages',
        'pagesLocal',
      ),
      tap(() => this.logger.info('process Menu WithPages')),
      map((menu) => {
        if (!menu) {
          return undefined;
        }

        const pagesLocal =
          (
            Array.isArray(menu) ? menu[0] : menu
          ).pagesLocal.map((page) => {
            const pageFile = page ? new FileCacheModel(page) : undefined;
            return pageFile
              ? this.domSanitizer.bypassSecurityTrustUrl(pageFile.objectUrl)
              : undefined;
          });

        return {
          ...(
            Array.isArray(menu) ? menu[0] : menu
          ),
          pagesLocal: pagesLocal.filter(isDefined),
        };
      }),
      tap(() => {
        this.loading$.next(false);
      }),
    );
  }

  public getMenuForCurrentMedia(): Observable<IMenuWithPages[] | null> {
    return combineLatest([
      this.intercom.currentMedia$,
      this.restaurantTable.table$.pipe(map(table => table?.clickableMediaConfig)),
    ]).pipe(
      switchMap(([currentMedia, config]) => {
        if (!config || !currentMedia) {
          return of(null);
        }

        const media = config.find((media) => {
          return media.id === currentMedia.id;
        });

        if (!media || !media.menuId) {
          return of(null);
        }

        return this.menusWithPages$.pipe(
         map(menus => {
           return menus?.filter(menu => {
             return menu.id === media.menuId || menu.originalId === media.menuId;
           })
         })
        )
      }),
      map((menu) => menu ? menu : null),
      take(1),
    );
  }

  public clear() {
    return this.menuRepository.all$().pipe(
      take(1),
      switchMap((menus) => {
        if (isUndefined(menus)) {
          return of([]);
        }

        const urls = menus.map((menu: IMenu) => [
          ...menu.pages,
          ...(menu.icon ? [menu.icon] : []),
          ...(menu.banner ? [menu.banner] : [])
        ]).flat();

        return combineLatest([
          this.menuRepository.clear$(),
          this.filesCache.bulkDelete(urls),
        ]).pipe(
          map(() => menus),
        );
      }),
    );
  }

  public sync(newMenus: IMenu[]) {
    return this.filesCache.downloadFiles(this.menuFileUrls(newMenus)).pipe(
      tapSubscribed(() => {
        this.sync$.next(true);
      }),
      switchMap(() => this.menuRepository.all$().pipe(take(1))),
      map((entry) => {
        const currentMenus = entry ?? [];

        return {
          add: arrayDiff(newMenus, currentMenus, arrayDiffIdCompare),
          update: currentMenus.reduce<ToUpdate<IMenu>[]>((acc, origin) => {
            const value = newMenus.find((menu) => menu.id === origin.id);
            const hasChanges = value && JSON.stringify(value) !== JSON.stringify(origin);

            if (value && hasChanges) {
              acc.push({ origin, value });
            }
            return acc;
          }, []),
          delete: arrayDiff(currentMenus, newMenus, arrayDiffIdCompare),
        };
      }),
      filter((changed) => {
        if (
          changed.add.length === 0 &&
          changed.update.length === 0 &&
          changed.delete.length === 0
        ) {
          this.sync$.next(false);
          return false;
        }

        return true;
      }),
      switchMap((changed) => forkJoin([
        this.toAdd(changed.add),
        this.toUpdate(changed.update),
        this.toDelete(changed.delete),
      ])),
      tap(() => {
        this.sync$.next(false);
      }),
    );
  }

  private menuFileUrls(menus: IMenu[]): string[] {
    return Array.from(new Set(menus.map((menu) => [
      ...menu.pages,
      ...(
        menu.icon ? [menu.icon] : []
      ),
      ...(
        menu.banner ? [menu.banner] : []
      ),
    ]).flat()));
  }

  private toAdd(menus: IMenu[]): Observable<IMenu[]> {
    if (menus.length === 0) {
      return of([]);
    }

    const menuFilesDownload$ = (menu: IMenu) => {
      const downloadMenuPages$ = this.filesCache.downloadFiles(menu.pages).pipe(
        tap(files => this.logger.debug(`Downloaded pages for menu ${menu.id}`, files))
      );

      const tasks: Observable<unknown>[] = [downloadMenuPages$];

      if (menu.icon) {
        const downloadMenuIcon$ = this.filesCache.downloadFile(menu.icon).pipe(
          tap(file => this.logger.debug(`Downloaded icon for menu ${menu.id}`, file))
        );
        tasks.push(downloadMenuIcon$);
      }

      if (menu.banner) {
        const downloadMenuBanner$ = this.filesCache.downloadFile(menu.banner).pipe(
          tap(file => this.logger.debug(`Downloaded banner for menu ${menu.id}`, file))
        );
        tasks.push(downloadMenuBanner$);
      }

      return forkJoin(tasks).pipe(
        switchMap(() => this.menuRepository.add$(menu)),
        map(() => menu)
      );
    };

    return of(menus).pipe(
      tap((menus) => this.logger.debug('Menus to add', menus)),
      switchMap((menus) => forkJoin(
        menus.map((menu) => menuFilesDownload$(menu)),
      )),
      tap((menus) => this.logger.debug('Menus added', menus)),
    );
  }

  private toUpdate(menus: ToUpdate<IMenu>[]): Observable<IMenu[]> {
    if (menus.length === 0) {
      return of([]);
    }

    return of(menus).pipe(
      tap((menus) => this.logger.debug('Menus to update', menus)),
      switchMap((menus) => forkJoin(
        menus.map((menu) => {
          const oldFiles = this.menuFileUrls([menu.origin]);
          const newFiles = this.menuFileUrls([menu.value]);
          const staleFiles = arrayDiff(oldFiles, newFiles);

          return this.filesCache.downloadFiles(newFiles).pipe(
            switchMap(() => this.menuRepository.update$(menu.value)),
            switchMap(() => this.filesCache.bulkDelete(staleFiles)),
            map(() => menu.value),
          );
        }),
      )),
      tap((menus) => this.logger.debug('Menus updated', menus)),
    );
  }

  private toDelete(menus: IMenu[]): Observable<unknown> {
    if (menus.length === 0) {
      return of([]);
    }

    return of(menus).pipe(
      tap((menus) => this.logger.debug('Menus to delete', menus)),
      switchMap((menus) => {
        return this.filesCache.bulkDelete(
          menus.map((menu) => {
            const files: string[] = [...menu.pages];

            if (menu.icon) {
              files.push(menu.icon);
            }

            if (menu.banner) {
              files.push(menu.banner);
            }

            return files;
          }).flat(),
        ).pipe(
          tap((data) => this.logger.debug('Files deleted from cache', data)),
          map(() => menus)
        )
      }),
      switchMap((menus) => from(menus).pipe(
        concatMap((menu) => {
          return this.menuRepository.delete$(menu.id);
        }),
      )),
    );
  }

}
